import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/pro-solid-svg-icons/faTimes';
import { faPlus } from '@fortawesome/pro-solid-svg-icons/faPlus';

import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { Button } from 'ui-components/components/button';

import { ASCENDING } from '../../orders-grid/columns/constants';
import GroupByInputDD from './group-by-dropdown';
import { GroupByEnums } from '../../orders-grid/group-by-enums';
import Global from '../../../store/global-selectors';
import { requestGetOrders } from '../../../store/orders/orders.actions';
import { orderGridSetGroupBys } from '../../../store/orders/grid/orders.grid.actions';

import messages from './messages';
import style from './style.module.less';

const GroupByPopover = ({ closePopover, onGroupByChanged }) => {
  const dispatch = useDispatch();
  const firstRender = useRef(true);
  const activeGroupBys = useSelector(Global.selectOrdersGridActiveGroupByEnums);

  const [selectedGroupBys, setSelectedGroupBys] = useState(
    activeGroupBys.length ? activeGroupBys.map((groupBy) => groupBy?.id).filter((id) => id != null) : [''],
  );

  // Since redux takes enums of the groupbys, filter out blank strings from local state,
  // then map; get the enum from allGroupBys object based on the string id in local state
  const updateGroupBysInRedux = useCallback(
    (newGroupBys) => {
      const updatedActiveGroupBys = newGroupBys
        .filter((groupBy) => groupBy)
        .map((newGroupBy) => {
          const groupBy = activeGroupBys.find((activeGroupBy) => activeGroupBy.id === newGroupBy);
          return (
            groupBy || {
              ...GroupByEnums[newGroupBy],
              orderByDirection: ASCENDING,
            }
          );
        });
      dispatch(orderGridSetGroupBys(updatedActiveGroupBys));
      onGroupByChanged();
    },
    [activeGroupBys, dispatch, onGroupByChanged],
  );

  useEffect(() => {
    if (!firstRender.current) {
      dispatch(requestGetOrders());
    }
    firstRender.current = false;
  }, [dispatch, activeGroupBys]);

  const addGroupBy = useCallback(() => setSelectedGroupBys((inputsArr) => [...inputsArr, '']), []);
  const removeGroupBy = useCallback(
    (index) => {
      const newGroupBys = selectedGroupBys.length === 1 ? [''] : selectedGroupBys.filter((_, idx) => idx !== index);
      setSelectedGroupBys(newGroupBys);
      updateGroupBysInRedux(newGroupBys);
    },
    [selectedGroupBys, updateGroupBysInRedux],
  );
  const editGroupBy = useCallback(
    (index, value) => {
      const newGroupBys = [...selectedGroupBys];
      newGroupBys[index] = value;
      setSelectedGroupBys(newGroupBys);
      updateGroupBysInRedux(newGroupBys);
    },
    [selectedGroupBys, updateGroupBysInRedux],
  );

  const unselectedGroupBys = Object.keys(GroupByEnums).filter((groupBy) => !selectedGroupBys.includes(groupBy));

  return (
    <>
      <div className={style['popover-title']}>
        <FormattedMessage {...messages.groupByText} />
        <Button className={style['icon-button']} onClick={closePopover} pendoTrackingId='/orders#group-by-close' theme='unstyled'>
          <FontAwesomeIcon className={style.icon} icon={faTimes} />
        </Button>
      </div>
      <div className={style['popover-body']}>
        <p>
          <b>
            <FormattedMessage {...messages.popoverSelectLabel} />
          </b>
        </p>
        {selectedGroupBys.map((input, index) => (
          <GroupByInputDD
            key={`groupByInput${index}`}
            groupBys={unselectedGroupBys}
            index={index}
            onChange={editGroupBy}
            onRemove={removeGroupBy}
            value={input}
          />
        ))}
        <Button
          className={style['combine-field-button']}
          disabled={selectedGroupBys.length >= 3 || (selectedGroupBys.length === 1 && selectedGroupBys[0] === '')}
          onClick={addGroupBy}
          pendoTrackingId='/orders#group-by-combine-with-another-field'
          theme='link'
        >
          <FontAwesomeIcon className={style.icon} icon={faPlus} />
          <FormattedMessage {...messages.popoverCombineField} />
        </Button>
      </div>
    </>
  );
};

GroupByPopover.propTypes = {
  closePopover: PropTypes.func.isRequired,
  onGroupByChanged: PropTypes.func.isRequired,
};

export default GroupByPopover;
